import { formatTargetPrice } from '../../Utils/formatTargetPrice';
import React, { useState, useEffect } from 'react';
import firebase from '../../Firebase/firebaseConnection';
import { Unlock, ChevronDown, ChevronUp, Target as TargetIcon, Globe, Tag, TrendingUp, ArrowUp, ArrowDown, Star, Percent, DollarSign } from 'react-feather';
import { useSettingsManager } from '../../Configuration/SettingsManager';
import NextStockTitleMark from "../../components/NextStockTitleMark";
import CompanyLogos from '../../components/CompanyLogos';
import Header from '../../Static/Header';
import Footer from "../../Static/Footer";
import './Top10.css';

import { useTranslation } from "react-i18next";

export default function Top10({ setPage }) {

  const { t, i18n } = useTranslation();

  const { user, loadUserAccess, handleSettingsPress } = useSettingsManager();
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('USA');
  const [selectedList, setSelectedList] = useState('Attractive');
  const [targets, setTargets] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);


  useEffect(() => {
    let isMounted = true;

    async function init() {
      try {
        await loadUserAccess(); // backend check-access
      } finally {
        if (isMounted) {
          setLoading(false);
          handleSelectionChange('USA', 'Attractive');
        }
      }
    }

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePages = (page) => {
    setPage(page);
  };

  const handleSelectionChange = (country, list) => {
    if (!country && !list) return;

    if (country) setSelectedCountry(country);
    if (list) setSelectedList(list);

    const finalCountry = country || selectedCountry;
    const finalList = list || selectedList;

    if (finalCountry && finalList) {
      const firebaseOptions = {
        CANADA: {
          Attractive: 'CADattractive',
          Growth: 'CADgrowth',
          Dividend: 'CADdividend',
          Value: 'CADvalue',
          Pricey: 'CADpricey',
        },
        USA: {
          Attractive: 'USattractive',
          Growth: 'USgrowth',
          Dividend: 'USdividend',
          Value: 'USvalue',
          Pricey: 'USpricey',
        },
      };

      const firebaseList = firebaseOptions[finalCountry]?.[finalList] || null;

      if (firebaseList) {
        finalList === 'Value'
          ? fetchTargetsForValueList(firebaseList, finalCountry)
          : fetchTargets(firebaseList, finalCountry);
      }
    }
  };

  const fetchTargets = async (target, country) => {
    try {
      const snapshot = await firebase.database().ref(target).once('value');
      const dataList = [];

      snapshot.forEach((item) => {
        const val = item.val();
        dataList.push({
          key: item.key,
          calculatedDividend: parseFloat(val.propertyCalculatedDividend).toFixed(2) || 0,
          currentPrice: parseFloat(val.propertyCurrentPrice).toFixed(2) || 0,
          dividendDollar: parseFloat(val.propertyDividendDollar) || 0,
          dividendPercent: parseFloat(val.propertyDividendPercent) || 0,
          growth: parseFloat(val.propertyGrowth).toFixed(2) || 0,
          newPrice: parseFloat(val.propertyNewPrice).toFixed(2) || 0,
          ps: parseFloat(val.propertyPS).toFixed(2) || 0,
          pricePercent: parseFloat(val.propertyPricePercent).toFixed(2) || 0,
          shortName: val.propertyShortName || 'Unknown',
          symbol: val.propertySymbol || 'Unknown',
          country,
        });
      });

      if (!['CADpricey', 'USpricey', 'CADvalue', 'USvalue'].includes(target)) {
        dataList.sort((a, b) => parseInt(b.key, 10) - parseInt(a.key, 10));
      }

      setTargets(dataList);
    } catch (error) {
      console.error('Error fetching targets:', error);
    }
  };

  const fetchTargetsForValueList = async (target, country) => {
    try {
      const snapshot = await firebase.database().ref(target).once('value');
      const dataList = [];

      snapshot.forEach((item) => {
        const val = item.val();
        dataList.push({
          key: item.key,
          propertyTrailingPE: parseFloat(val.propertyTrailingPE).toFixed(2) || 0,
          propertyMarketCap: parseFloat(val.propertyMarketCap) || 0,
          shortName: val.propertyShortName || 'Unknown',
          symbol: val.propertySymbol || 'Unknown',
          country,
        });
      });

      setTargets(dataList);
    } catch (error) {
      console.error('Error fetching targets (Value list):', error);
    }
  };

  // const normalizedPlanType = (userPlanTypeFromStorage || '').toLowerCase();
  // const isPremiumActive = normalizedPlanType === 'premium' && !isTrialPeriod;
  // const isPremiumActive = user.hasTop10Access === true;
  const isPremiumActive =
    user.hasTop10Access === true ||
    (user.planType === 'premium' && user.trial === true);


  return (
    <div className="top10-container">

      <Header setPage={handlePages} />

      <div className="top10-content">
        {loading ? (
          <div className="top10-loading">
            <div className="top10-spinner"></div>
          </div>
        ) : (
          <div className="top10-box">
            <div style={{ display: "flex", flexDirection: "row" }}>
              <NextStockTitleMark className="top10-logo-next-stock" />
              <h2 className="top10-title">{t("top10.title")}</h2>
              <NextStockTitleMark className="top10-logo-next-stock" />
            </div>

            <p style={{ margin: "-5px" }}>
              <strong>{t("top10.usaCanada")}</strong>
            </p>

            {user.planType === "premium" && user.trial ? (
              <p style={{ marginBottom: "30px", fontSize: "12px" }}>
                {t("top10.plan")}: <strong>{t("top10.freeTrial")}</strong>
              </p>
            ) : (
              <p style={{ marginBottom: "30px", fontSize: "12px" }}>
                {t("top10.plan")}:{" "}
                <strong>{(user.planType || "free").toUpperCase()}</strong>
              </p>
            )}

            {isPremiumActive ? (
              <div className="top10-selectors">
                <select
                  value={selectedCountry}
                  onChange={(e) => handleSelectionChange(e.target.value, selectedList)}
                >
                  <option value="">{t("top10.selectCountry")}</option>
                  <option value="CANADA">{t("top10.canada")}</option>
                  <option value="USA">{t("top10.usa")}</option>
                </select>

                <select
                  value={selectedList}
                  onChange={(e) => handleSelectionChange(selectedCountry, e.target.value)}
                >
                  <option value="">{t("top10.selectList")}</option>
                  <option value="Attractive">{t("top10.attractive")}</option>
                  <option value="Growth">{t("top10.growth")}</option>
                  <option value="Dividend">{t("top10.dividend")}</option>
                  <option value="Value">{t("top10.value")}</option>
                  <option value="Pricey">{t("top10.pricey")}</option>
                </select>
              </div>
            ) : (
              <button className="top10-upgrade-btn" onClick={handleSettingsPress}>
                <Unlock /> {t("top10.upgradePlan")}
              </button>
            )}

            {isPremiumActive && (
              <h3>
                📈 {t("top10.targetsList")} ({selectedCountry || " ... "}/
                {selectedList || " ... "})
              </h3>
            )}

            {isPremiumActive && (
              <div className="top10-list-container">
                <ul>
                  {targets.map((item, index) => (
                    <li key={item.key} className="top10-card-li" style={{ "--stock-index": index }}>
                      <div className="top10-cards-area">
                        <div className="top10-main-header">
                          <div className="top10-card-title-row">
                            <div className="rank-badge">{index + 1}</div>

                            <h4>
                              {item.shortName} ({item.symbol})
                            </h4>
                          </div>

                          <div className="top10-logo-area">
                            <CompanyLogos symbol={item.symbol} />
                          </div>
                        </div>

                        {selectedList !== "Value" ? (
                          <div className="top10-main-metrics">

                            <div className="metric-box analysis-price">
                              <TrendingUp size={30} />
                              <div>
                                <span>{t("top10.analysisPrice")}</span>
                                <strong>{item.currentPrice ? `$${item.currentPrice}` : "-"}</strong>
                              </div>
                            </div>


                            <div className="metric-box target-price">
                              <TargetIcon size={30} />
                              <div>
                                <span>{t("top10.priceTarget")}</span>
                                <strong>${formatTargetPrice(item.newPrice)}</strong>
                              </div>
                            </div>


                            <div className={`metric-box upside ${selectedList === "Pricey" ? "downside" : ""}`}>
                              {selectedList === "Pricey" ? (
                                <ArrowDown size={30} />
                              ) : (
                                <ArrowUp size={30} />
                              )}
                              <div>
                                <span>
                                  {selectedList === "Growth"
                                    ? t("top10.growthValue")
                                    : selectedList === "Dividend"
                                      ? t("top10.percentDividend")
                                      : selectedList === "Pricey"
                                        ? t("top10.percentDownside")
                                        : t("top10.percentUpside")}
                                </span>

                                <strong>
                                  {selectedList === "Dividend"
                                    ? `${parseFloat(item.dividendPercent * 100).toFixed(1)}%`
                                    : selectedList === "Growth"
                                      ? `${item.growth ?? "n/a"}%`
                                      : `${item.pricePercent ?? "n/a"}%`}
                                </strong>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="top10-main-metrics">
                            <div className="metric-box">
                              <DollarSign size={30} />
                              <div>
                                <span>{t("top10.marketCap")}</span>
                                <strong>
                                  $
                                  {parseFloat(
                                    item.propertyMarketCap / 1000000000
                                  ).toFixed(2)}{" "}
                                  B
                                </strong>
                              </div>
                            </div>

                            <div className="metric-box">
                              <TrendingUp size={30} />
                              <div>
                                <span>{t("top10.peRatio")}</span>
                                <strong>{item.propertyTrailingPE}</strong>
                              </div>
                            </div>

                            <div className="metric-box">
                              <Tag size={30} />
                              <div>
                                <span>{t("target.symbol")}</span>
                                <strong>{item.symbol}</strong>
                              </div>
                            </div>
                          </div>
                        )}

                        <button
                          className={`top10-expand-btn-new ${expandedCard === item.key ? "is-open" : ""
                            }`}
                          onClick={() =>
                            setExpandedCard(expandedCard === item.key ? null : item.key)
                          }
                        >
                          {expandedCard === item.key ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </button>


                        {expandedCard === item.key && (
                          <div className="top10-expanded">
                            <div className="expanded-item">
                              <Percent size={28} />
                              <div>
                                <span>{t("top10.percentDividend")}</span>
                                <strong>
                                  {item.dividendPercent
                                    ? `${parseFloat(item.dividendPercent * 100).toFixed(1)}%`
                                    : "-"}
                                </strong>
                              </div>
                            </div>

                            <div className="expanded-item">
                              <Globe size={28} />
                              <div>
                                <span>{t("target.country")}</span>
                                <strong>
                                  {item.country === "CANADA"
                                    ? "🇨🇦 Canada"
                                    : "🇺🇸 USA"}
                                </strong>
                              </div>
                            </div>

                            <div className="expanded-item">
                              <Tag size={28} />
                              <div>
                                <span>{t("target.symbol")}</span>
                                <strong>{item.symbol}</strong>
                              </div>
                            </div>

                            <div className="expanded-item">
                              <TrendingUp size={28} />
                              <div>
                                <span>P/S Ratio</span>
                                <strong>{item.ps || "n/a"}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );


}
